/*
 * Copyright (c) 2021. FINN Sp. z o.o.
 */

package pl.finn.forms.utils.form

import io.kvision.form.DateFormControl
import io.kvision.form.Form
import io.kvision.form.FormControl
import io.kvision.form.FormPanel
import io.kvision.form.check.CheckBox
import io.kvision.i18n.I18n
import io.kvision.panel.FieldsetPanel
import io.kvision.types.DateSerializer
import io.kvision.types.LocalDate
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.KSerializer
import kotlinx.serialization.modules.SerializersModule
import pl.finn.forms.tw1.DocumentTreeNode
import kotlin.js.Date
import kotlin.reflect.KProperty1

internal data class FormParams<in F : FormControl>(
  val required: Boolean = false,
  val requiredMessage: String? = null,
  val validatorMessage: ((F) -> String?)? = null,
  val validator: ((F) -> Boolean?)? = null
)

@OptIn(ExperimentalSerializationApi::class)
@Suppress("TooManyFunctions", "UnsafeCastFromDynamic")
class ExtendedForm<K : Any>(
  private val panel: FormBuilder<K>? = null,
  private val serializer: KSerializer<K>? = null,
) {

  private val modelFactory: ((Map<DocumentTreeNode?, String?>) -> K)? = serializer?.let {
    {
      val json = js("{}")
      it.forEach { (key, value) ->
        key!!.value = value
      }
      kotlinx.serialization.json.Json {
        encodeDefaults = true
        ignoreUnknownKeys = true
        serializersModule = SerializersModule {
          contextual(Date::class, DateSerializer)
        }
      }.decodeFromString(serializer, JSON.stringify(json))
    }
  }

  private val fields: MutableMap<DocumentTreeNode?, FormControl> = mutableMapOf()
  private val fieldsParams: MutableMap<DocumentTreeNode?, Any> = mutableMapOf()
  private var validatorMessage: ((ExtendedForm<K>) -> String?)? = null
  internal var validator: ((ExtendedForm<K>) -> Boolean?)? = null

  fun getControl(key: KProperty1<K, *>): FormControl? {
    return this.fields[key.name as DocumentTreeNode]
  }

  operator fun get(key: KProperty1<K, *>): Any? {
    return getControl(key)?.getValue()
  }

  fun getControlField(key: DocumentTreeNode?): FormControl? {
    return this.fields[key]
  }

  fun getData(): K {
    val map = fields.entries.associateBy({ it.key }, { it.value.getValueAsString() })
    return modelFactory?.invoke(map.withDefault { null }) ?: throw IllegalStateException("Serializer not defined")
  }

  fun validate(markFields: Boolean = true): Boolean {
    val fieldWithError = fieldsParams.mapNotNull { entry ->
      fields[entry.key]?.let { control ->
        @Suppress("UNCHECKED_CAST")
        val fieldsParams = (entry.value as? FormParams<FormControl>)
        val required = fieldsParams?.required ?: false
        val controlValue = control.getValue() == null || control.getValue() == ""
        val requiredError = controlValue && control.visible && required
        if (requiredError) {
          if (markFields) control.validatorError =
            I18n.trans(fieldsParams?.requiredMessage) ?: "Wartość pola jest wymagana"
          true
        } else {
          var isValidator = if (!controlValue && fieldsParams?.validatorMessage != null) fieldsParams.validator?.invoke(control) else true
          if (controlValue && fieldsParams?.validator == null) isValidator = true
          val validatorPassed = !control.visible || isValidator == true
          if (markFields) {
            control.validatorError = if (!validatorPassed) {
              I18n.trans(fieldsParams?.validatorMessage?.invoke(control)) ?: "Niewłaściwy format wprowadzonych danych"
            } else {
              null
            }
          }
          !validatorPassed
        }
      }
    }.find { it }
    panel?.validatorError = null
    val validatorPassed = validator?.invoke(this) ?: true
    panel?.validatorError = if (!validatorPassed) {
      I18n.trans(validatorMessage?.invoke(this)) ?: "Invalid form data"
    } else {
      null
    }
    return fieldWithError == null && validatorPassed
  }

  internal fun <C : FormControl> addInternal(
    key: DocumentTreeNode,
    control: C,
    required: Boolean = false,
    requiredMessage: String? = null,
    validatorMessage: ((C) -> String?)? = null,
    validator: ((C) -> Boolean?)? = null
  ): ExtendedForm<K> {
    setValueControl(key, control)
    this.fields[key] = control
    this.fieldsParams[key] = FormParams(required, requiredMessage, validatorMessage, validator)
    return this
  }

  /**
   * Removes a control from the form.
   * @param key key identifier of the control
   * @return current form
   */
  fun remove(key: DocumentTreeNode): ExtendedForm<K> {
    this.fields.remove(key)
    return this
  }

  private fun setValueControl(key: DocumentTreeNode, control: FormControl) {
    when(control) {
//      is DateFormControl -> control.setValue(LocalDate())
      is CheckBox -> control.setValue(key.value.toBoolean())
      else -> control.setValue(key.value)
    }
  }
}
